import { useCallback, useEffect, useState } from "react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Skeleton } from "@/components/ui/skeleton";
import { Scissors, AudioLines, Video, Megaphone, Trash2, Clock, Globe, Check } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { SectionLoader } from "@/components/common";
import { SocialPostGenerator, SocialPostDialog } from "@/components/social/SocialPostGenerator";

const TYPE_META = {
  clip: { icon: Scissors, label: "Clips" },
  recording: { icon: AudioLines, label: "Recordings" },
  video: { icon: Video, label: "Videos" },
};

function fmtDate(s) {
  if (!s) return "";
  try { return new Date(s).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }); }
  catch { return s; }
}

function buildSource(item) {
  const src = { type: item.type, id: item.id, title: item.title, public_url: null };
  if (item.type === "clip" && item.is_public && item.public_token) {
    src.public_url = `${window.location.origin}/clip/${item.public_token}`;
  }
  return src;
}

export default function SocialStudio() {
  const [sources, setSources] = useState(null);
  const [tab, setTab] = useState("clip");
  const [selected, setSelected] = useState(null);
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(true);
  const [viewPost, setViewPost] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  const loadSources = useCallback(async () => {
    try {
      const r = await api.get("/social/sources");
      setSources(r.data);
      // default the active tab to the first non-empty bucket
      const firstNonEmpty = ["clip", "recording", "video"].find((t) => (r.data[`${t}s`] || []).length > 0);
      if (firstNonEmpty) setTab(firstNonEmpty);
    } catch {
      setSources({ clips: [], recordings: [], videos: [] });
    }
  }, []);

  const loadHistory = useCallback(async () => {
    setHistoryLoading(true);
    try {
      const r = await api.get("/social/posts");
      setHistory(r.data || []);
    } catch { setHistory([]); }
    finally { setHistoryLoading(false); }
  }, []);

  useEffect(() => { loadSources(); loadHistory(); }, [loadSources, loadHistory]);

  const deletePost = async (postId) => {
    try {
      await api.delete(`/social/posts/${postId}`);
      setHistory((prev) => prev.filter((p) => p.post_id !== postId));
      toast.success("Generation deleted");
    } catch { toast.error("Could not delete"); }
  };

  if (!sources) return <SectionLoader label="Loading content…" />;

  const buckets = { clip: sources.clips || [], recording: sources.recordings || [], video: sources.videos || [] };
  const totalSources = buckets.clip.length + buckets.recording.length + buckets.video.length;

  return (
    <div className="space-y-6" data-testid="social-studio">
      <div>
        <h1 className="text-2xl font-bold text-foreground">Social posts</h1>
        <p className="text-sm text-muted-foreground">Turn any clip, recording, or video into ready-to-paste posts for LinkedIn, X, Instagram & Facebook.</p>
      </div>

      <div className="grid gap-5 lg:grid-cols-[340px_1fr]">
        {/* Source picker */}
        <Card className="border-border">
          <CardContent className="p-3">
            <p className="mb-2 px-1 text-xs font-semibold uppercase tracking-wider text-muted-foreground">1. Choose content</p>
            {totalSources === 0 ? (
              <div className="rounded-lg border border-dashed border-border p-6 text-center text-sm text-muted-foreground" data-testid="social-no-sources">
                No clips, recordings, or videos yet. Create some in the Recordings or Videos tabs first.
              </div>
            ) : (
              <Tabs value={tab} onValueChange={setTab}>
                <TabsList className="grid w-full grid-cols-3">
                  {["clip", "recording", "video"].map((t) => {
                    const M = TYPE_META[t];
                    return (
                      <TabsTrigger key={t} value={t} data-testid={`social-srctab-${t}`} className="text-xs">
                        <M.icon className="mr-1 h-3.5 w-3.5" />{M.label}
                        <span className="ml-1 text-muted-foreground">{buckets[t].length}</span>
                      </TabsTrigger>
                    );
                  })}
                </TabsList>
                {["clip", "recording", "video"].map((t) => {
                  const M = TYPE_META[t];
                  return (
                    <TabsContent key={t} value={t} className="mt-3">
                      {buckets[t].length === 0 ? (
                        <p className="px-1 py-6 text-center text-sm text-muted-foreground">No {M.label.toLowerCase()} yet.</p>
                      ) : (
                        <ScrollArea className="h-[360px] pr-2">
                          <div className="space-y-1.5">
                            {buckets[t].map((item) => {
                              const isSel = selected?.type === item.type && selected?.id === item.id;
                              return (
                                <button
                                  key={item.id}
                                  onClick={() => setSelected(buildSource(item))}
                                  className={`flex w-full items-start gap-2 rounded-lg border p-2.5 text-left transition-colors ${isSel ? "border-primary bg-primary/5" : "border-border hover:bg-muted/60"}`}
                                  data-testid={`social-source-item-${item.id}`}
                                >
                                  <M.icon className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
                                  <span className="min-w-0 flex-1">
                                    <span className="block truncate text-sm font-medium text-foreground">{item.title || "Untitled"}</span>
                                    <span className="block truncate text-xs text-muted-foreground">{item.subtitle}</span>
                                  </span>
                                  {item.type === "clip" && item.is_public && <Globe className="mt-0.5 h-3.5 w-3.5 shrink-0 text-primary" title="Public link available" />}
                                  {isSel && <Check className="mt-0.5 h-4 w-4 shrink-0 text-primary" />}
                                </button>
                              );
                            })}
                          </div>
                        </ScrollArea>
                      )}
                    </TabsContent>
                  );
                })}
              </Tabs>
            )}
          </CardContent>
        </Card>

        {/* Generator */}
        <div>
          <p className="mb-2 px-1 text-xs font-semibold uppercase tracking-wider text-muted-foreground">2. Generate & copy</p>
          {selected ? (
            <SocialPostGenerator source={selected} onGenerated={loadHistory} />
          ) : (
            <div className="flex h-full min-h-[260px] flex-col items-center justify-center rounded-xl border border-dashed border-border p-8 text-center" data-testid="social-pick-prompt">
              <Megaphone className="h-8 w-8 text-muted-foreground" />
              <p className="mt-2 text-sm font-medium text-foreground">Pick a clip, recording, or video</p>
              <p className="text-sm text-muted-foreground">Then choose a tone & platforms and generate.</p>
            </div>
          )}
        </div>
      </div>

      {/* Recent generations */}
      <div>
        <h2 className="mb-2 text-sm font-semibold uppercase tracking-wider text-muted-foreground">Recent generations</h2>
        {historyLoading ? (
          <div className="grid gap-2 sm:grid-cols-2">{[0, 1].map((i) => <Skeleton key={i} className="h-16 w-full" />)}</div>
        ) : history.length === 0 ? (
          <p className="text-sm text-muted-foreground" data-testid="social-history-empty">No posts generated yet.</p>
        ) : (
          <div className="grid gap-2 sm:grid-cols-2">
            {history.map((p) => {
              const M = TYPE_META[p.source_type] || TYPE_META.clip;
              return (
                <Card key={p.post_id} className="border-border" data-testid={`social-history-${p.post_id}`}>
                  <CardContent className="flex items-center gap-3 p-3">
                    <M.icon className="h-4 w-4 shrink-0 text-primary" />
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium text-foreground">{p.source_title || "Untitled"}</p>
                      <p className="flex items-center gap-2 text-xs text-muted-foreground">
                        <Clock className="h-3 w-3" />{fmtDate(p.created_at)}
                        <span className="capitalize">· {p.tone}</span>
                        <span>· {(p.platforms || []).length} platforms</span>
                      </p>
                    </div>
                    <Button size="sm" variant="secondary" onClick={() => { setViewPost(p); setDialogOpen(true); }} data-testid={`social-history-open-${p.post_id}`}>Open</Button>
                    <Button size="icon" variant="ghost" className="h-8 w-8 text-muted-foreground hover:text-red-600" onClick={() => deletePost(p.post_id)} aria-label="Delete generation"><Trash2 className="h-4 w-4" /></Button>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>

      <SocialPostDialog open={dialogOpen} onOpenChange={setDialogOpen} initialPost={viewPost} />
    </div>
  );
}
